import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import RosterGuardPanel from '@/components/RosterGuardPanel';
import {
  Calendar,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Users,
  Loader2,
  RefreshCw,
  Lock,
  Unlock,
  FileText,
  Stethoscope,
  Plane,
  Sparkles,
  MapPin,
  Briefcase,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useLocationUrlSync } from '@/contexts/LocationContext';
import { formatHoursHM, getISOWeek, navigateIsoWeek } from '@/lib/format';

const PERIOD_META = {
  open: { label: 'Open', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: Calendar },
  locked: { label: 'Definitief', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Lock },
  exported: { label: 'In verloning', color: 'bg-brand-amber/15 text-brand-amber-dark border-brand-amber/30', icon: FileText },
};

const LEAVE_META = {
  ziekte: { label: 'Ziek', color: 'bg-rose-50 text-rose-700 border-rose-200', icon: Stethoscope },
  vakantie: { label: 'Vakantie', color: 'bg-sky-50 text-sky-700 border-sky-200', icon: Plane },
  bijzonder_verlof: { label: 'Bijz. verlof', color: 'bg-violet-50 text-violet-700 border-violet-200', icon: Sparkles },
};

const SHIFT_STATUS_META = {
  planned: { label: 'Gepland', color: 'bg-slate-100 text-slate-600' },
  worked: { label: 'Gewerkt', color: 'bg-emerald-100 text-emerald-700' },
  approved: { label: 'Goedgekeurd', color: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Niet versch.', color: 'bg-rose-100 text-rose-700' },
};

function PeriodBadge({ status }) {
  const meta = PERIOD_META[status] || PERIOD_META.open;
  const Icon = meta.icon;
  return (
    <span
      data-testid={`period-status-${status}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${meta.color}`}
    >
      <Icon size={12} />
      {meta.label}
    </span>
  );
}

function KpiTile({ icon: Icon, label, value, sublabel, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            {value}
          </p>
          {sublabel && <p className="mt-0.5 text-[11px] text-slate-500">{sublabel}</p>}
        </div>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${accent || 'bg-brand-amber/15 text-brand-amber-dark'}`}>
          <Icon size={16} />
        </div>
      </div>
    </div>
  );
}

function DrilldownRow({ employee, year, week, locationId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get('/weekoverzicht/drilldown', {
          params: { location_id: locationId, year, week, employee_id: employee.employee_id },
        });
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) toast({ title: 'Drill-down laden mislukt', variant: 'destructive' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [employee.employee_id, locationId, year, week, toast]);

  if (loading) {
    return (
      <div className="px-4 py-6 text-center text-slate-500 flex items-center justify-center gap-2 bg-slate-50">
        <Loader2 size={14} className="animate-spin" /> Laden…
      </div>
    );
  }
  if (!data?.days) return null;

  return (
    <div className="bg-slate-50 px-6 py-4 border-y border-slate-200" data-testid={`drilldown-${employee.personeelsnummer}`}>
      <div className="grid grid-cols-7 gap-2">
        {data.days.map((d) => (
          <div
            key={d.date}
            className={`rounded-md border p-2 text-xs ${
              d.has_activity ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50'
            }`}
            data-testid={`drilldown-day-${d.date}`}
          >
            <div className="flex justify-between items-baseline mb-1.5 border-b border-slate-100 pb-1">
              <span className="font-semibold text-slate-700">{d.day_name_nl.slice(0, 2)}</span>
              <span className="text-[10px] text-slate-400 tabular-nums">{d.date.slice(8)}</span>
            </div>
            {!d.has_activity ? (
              <p className="text-[10px] text-slate-300 italic">—</p>
            ) : (
              <div className="space-y-1.5">
                {d.entries.map((entry, idx) => {
                  // Stable composite key: prefer entity id, else kind+timestamp, else fall-back to index
                  const entryKey = entry.id
                    || entry.assignment_id
                    || `${entry.kind}-${entry.actual_clock_in || entry.start_time || entry.leave_type || idx}`;
                  if (entry.kind === 'shift') {
                    const stat = SHIFT_STATUS_META[entry.status] || SHIFT_STATUS_META.planned;
                    const planned = `${entry.start_time}–${entry.end_time}`;
                    const actual = entry.actual_clock_in
                      ? `${entry.actual_clock_in}–${entry.actual_clock_out || 'open'}`
                      : null;
                    const mismatch = actual && planned !== actual;
                    return (
                      <div key={entryKey} className="rounded border border-slate-200 p-1.5 bg-white">
                        <div className="flex items-center justify-between gap-1">
                          <span className="tabular-nums font-medium text-slate-900 text-[11px]">{planned}</span>
                          <span className={`rounded px-1 py-0 text-[9px] font-medium ${stat.color}`}>{stat.label}</span>
                        </div>
                        {actual && (
                          <div className={`mt-0.5 flex items-center gap-1 text-[10px] tabular-nums ${mismatch ? 'text-amber-700' : 'text-emerald-600'}`}>
                            <Clock size={9} />
                            {actual}
                            {mismatch && <span className="text-[9px]">(≠)</span>}
                          </div>
                        )}
                        {entry.is_hulpvraag && (
                          <div className="mt-0.5 text-[9px] text-rose-600">Hulpvraag</div>
                        )}
                        {entry.source === 'ad_hoc_clock' && (
                          <div className="mt-0.5 text-[9px] text-slate-400">ad-hoc</div>
                        )}
                      </div>
                    );
                  }
                  if (entry.kind === 'clock_only') {
                    return (
                      <div key={entryKey} className="rounded border border-amber-200 bg-amber-50 p-1.5">
                        <div className="flex items-center gap-1 text-[10px] text-amber-700">
                          <AlertCircle size={9} />
                          <span className="font-medium">Klok zonder shift</span>
                        </div>
                        <div className="mt-0.5 tabular-nums text-[10px] text-amber-700">
                          {entry.actual_clock_in}–{entry.actual_clock_out || 'open'}
                        </div>
                      </div>
                    );
                  }
                  if (entry.kind === 'leave') {
                    const meta = LEAVE_META[entry.leave_type] || LEAVE_META.bijzonder_verlof;
                    const Icon = meta.icon;
                    return (
                      <div key={entryKey} className={`rounded border p-1.5 ${meta.color}`}>
                        <div className="flex items-center gap-1 text-[10px]">
                          <Icon size={9} />
                          <span className="font-medium">{meta.label}</span>
                        </div>
                        {entry.hours > 0 && (
                          <div className="mt-0.5 tabular-nums text-[10px]">{formatHoursHM(entry.hours)}</div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Weekoverzicht() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { slug: selectedSlug, setSlug: setSelectedSlug } = useLocationUrlSync();
  const isOwnerAdmin = user && (user.role === 'owner' || user.role === 'admin');

  const [locations, setLocations] = useState([]);
  const initial = getISOWeek();
  const [year, setYear] = useState(initial.year);
  const [week, setWeek] = useState(initial.week);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [lockBusy, setLockBusy] = useState(false);
  const [confirmLock, setConfirmLock] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: locs } = await api.get('/locations');
        const eligible = (locs || [])
          .filter((l) => l.has_roster !== false)
          .sort((a, b) => a.name.localeCompare(b.name, 'nl'));
        setLocations(eligible);
      } catch {
        toast({ title: 'Kon locaties niet laden', variant: 'destructive' });
      }
    })();
  }, [toast]);

  // Fallback selection (URL-aware via context).
  useEffect(() => {
    if (locations.length > 0 && !selectedSlug) {
      setSelectedSlug(locations[0].slug);
    }
  }, [locations, selectedSlug, setSelectedSlug]);

  const loadWeek = useCallback(async () => {
    if (!selectedSlug) return;
    setLoading(true);
    setExpanded(null);
    try {
      const { data: resp } = await api.get('/weekoverzicht', {
        params: { location_id: selectedSlug, year, week },
      });
      setData(resp);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Kon weekoverzicht niet laden';
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedSlug, year, week, toast]);

  useEffect(() => { loadWeek(); }, [loadWeek]);

  const shiftWeek = (delta) => {
    const { year: y, week: w } = navigateIsoWeek(year, week, delta);
    setWeek(w);
    setYear(y);
  };

  const periodStatus = data?.period_status || 'open';
  const isLocked = periodStatus === 'locked' || periodStatus === 'exported';

  const doLock = async () => {
    setConfirmLock(false);
    setLockBusy(true);
    try {
      await api.post('/payroll/lock', { location_id: selectedSlug, year, week });
      toast({ title: 'Periode afgesloten', description: `Week ${week}/${year} is nu definitief` });
      await loadWeek();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Lock mislukt';
      toast({ title: detail, variant: 'destructive' });
    } finally {
      setLockBusy(false);
    }
  };

  const doUnlock = async () => {
    setLockBusy(true);
    try {
      await api.post('/payroll/unlock', { location_id: selectedSlug, year, week });
      toast({ title: 'Periode ontgrendeld', description: `Week ${week}/${year} is weer bewerkbaar` });
      await loadWeek();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Unlock mislukt';
      toast({ title: detail, variant: 'destructive' });
    } finally {
      setLockBusy(false);
    }
  };

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const diffColor = (v) => {
    if (v > 0.5) return 'text-emerald-600';
    if (v < -0.5) return 'text-rose-600';
    return 'text-slate-500';
  };

  return (
    <div className="space-y-5" data-testid="page-weekoverzicht">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Weekoverzicht</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Retrospectief overzicht per medewerker: gepland vs gewerkt vs verzuim.
            </p>
          </div>
          {data && <PeriodBadge status={periodStatus} />}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid="weekoverzicht-location-select"
            value={selectedSlug}
            onChange={(e) => setSelectedSlug(e.target.value)}
            disabled={loading}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm focus:border-brand-amber focus:outline-none disabled:opacity-50"
          >
            {locations.map((loc) => (
              <option key={loc.slug} value={loc.slug}>{loc.name}</option>
            ))}
          </select>
          <div className="inline-flex items-center rounded-md border border-slate-200 bg-white">
            <button
              type="button"
              data-testid="weekoverzicht-week-prev"
              onClick={() => shiftWeek(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-l-md text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              aria-label="Vorige week"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="px-3 py-1.5 text-sm font-medium text-slate-700" data-testid="weekoverzicht-week-label">
              Week {week} / {year}
            </div>
            <button
              type="button"
              data-testid="weekoverzicht-week-next"
              onClick={() => shiftWeek(1)}
              className="flex h-9 w-9 items-center justify-center rounded-r-md text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              aria-label="Volgende week"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <Button type="button" variant="outline" size="sm" data-testid="weekoverzicht-refresh" onClick={loadWeek} disabled={loading} className="h-9">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span className="ml-1.5">Verversen</span>
          </Button>
          {!isLocked ? (
            <Button
              type="button"
              size="sm"
              data-testid="weekoverzicht-lock-btn"
              onClick={() => setConfirmLock(true)}
              disabled={lockBusy || loading || rows.length === 0}
              className="h-9 bg-brand-navy hover:bg-brand-navy/90 text-white"
            >
              {lockBusy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
              <span className="ml-1.5">Maak definitief</span>
            </Button>
          ) : isOwnerAdmin ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="weekoverzicht-unlock-btn"
              onClick={doUnlock}
              disabled={lockBusy || loading}
              className="h-9 border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              {lockBusy ? <Loader2 size={14} className="animate-spin" /> : <Unlock size={14} />}
              <span className="ml-1.5">Ontgrendel</span>
            </Button>
          ) : null}
        </div>
      </div>

      {/* Locked banner */}
      {isLocked && data?.period && (
        <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="weekoverzicht-locked-banner">
          <Lock size={14} />
          <span>
            Deze periode is <strong>{periodStatus === 'exported' ? 'geëxporteerd naar verloning' : 'definitief gemaakt'}</strong>
            {data.period.locked_at && ` op ${new Date(data.period.locked_at).toLocaleDateString('nl-NL')}`}.
            Wijzigingen zijn geblokkeerd (alleen owner/admin kan ontgrendelen).
          </span>
        </div>
      )}

      {/* Roster min/max-uren guard (N2) */}
      {data && selectedSlug && (
        <RosterGuardPanel locationSlug={selectedSlug} year={year} week={week} />
      )}

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <KpiTile icon={Users} label="Medewerkers" value={totals.employees || 0} sublabel="actief deze week" accent="bg-brand-navy/10 text-brand-navy" />
        <KpiTile icon={Briefcase} label="Contract uren" value={formatHoursHM(totals.contract_hours || 0)} sublabel="som contracten" />
        <KpiTile icon={Calendar} label="Gepland" value={formatHoursHM(totals.gepland_uren || 0)} sublabel="alle shifts" accent="bg-slate-100 text-slate-700" />
        <KpiTile icon={CheckCircle2} label="Gewerkt" value={formatHoursHM(totals.gewerkt_uren || 0)} sublabel="goedgekeurd" accent="bg-emerald-100 text-emerald-700" />
        <KpiTile icon={TrendingUp} label="Verschil" value={`${totals.verschil_uren > 0 ? '+' : ''}${formatHoursHM(totals.verschil_uren || 0)}`} sublabel="gewerkt − contract" accent={totals.verschil_uren < 0 ? 'bg-rose-100 text-rose-700' : 'bg-brand-amber/15 text-brand-amber-dark'} />
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="weekoverzicht-table">
        {loading ? (
          <div className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Laden…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <CalendarDays size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Geen activiteit voor deze week.</p>
            <p className="mt-1 text-xs text-slate-400">Plan shifts via Roosterplanner of registreer verzuim.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-[40px]"></TableHead>
                <TableHead className="w-[260px]">Medewerker</TableHead>
                <TableHead className="text-right">Contract</TableHead>
                <TableHead className="text-right">Gepland</TableHead>
                <TableHead className="text-right">Gewerkt</TableHead>
                <TableHead className="text-right">Ziek</TableHead>
                <TableHead className="text-right">Vakantie</TableHead>
                <TableHead className="text-right">Bijz. verlof</TableHead>
                <TableHead className="text-right">Verschil</TableHead>
                <TableHead className="text-right">Km totaal</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const isOpen = expanded === row.employee_id;
                return (
                  <React.Fragment key={row.employee_id}>
                    <TableRow
                      data-testid={`weekoverzicht-row-${row.personeelsnummer}`}
                      className="cursor-pointer hover:bg-slate-50/70"
                      onClick={() => setExpanded(isOpen ? null : row.employee_id)}
                    >
                      <TableCell className="text-center">
                        <button
                          type="button"
                          data-testid={`expand-${row.personeelsnummer}`}
                          className="flex h-6 w-6 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                          aria-label={isOpen ? 'Inklappen' : 'Uitklappen'}
                        >
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRightIcon size={14} />}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-navy/10 text-[10px] font-semibold text-brand-navy">
                            {row.name?.split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase() || '?'}
                          </div>
                          <div className="leading-tight">
                            <p className="text-sm font-medium text-slate-900">{row.name}</p>
                            <p className="text-[11px] text-slate-500">
                              #{row.personeelsnummer} · {row.contract_type || 'vast'} · {row.role || 'medewerker'}
                            </p>
                          </div>
                          {row.rejected_count > 0 && (
                            <Badge variant="outline" className="ml-1 border-rose-300 bg-rose-50 text-rose-700 text-[10px]">
                              {row.rejected_count} no-show
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums text-slate-600">
                        {formatHoursHM(row.contract_hours)}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {formatHoursHM(row.gepland_uren)}
                        {row.coverage_pct !== null && (
                          <span className="ml-1 text-[10px] text-slate-400">{row.coverage_pct}%</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums text-emerald-700 font-medium">
                        {formatHoursHM(row.gewerkt_uren)}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {row.ziekte_uren > 0 ? <span className="text-rose-600">{formatHoursHM(row.ziekte_uren)}</span> : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {row.vakantie_uren > 0 ? <span className="text-sky-600">{formatHoursHM(row.vakantie_uren)}</span> : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {row.bijz_verlof_uren > 0 ? <span className="text-violet-600">{formatHoursHM(row.bijz_verlof_uren)}</span> : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className={`text-right text-sm font-semibold tabular-nums ${diffColor(row.verschil_uren)}`}>
                        {row.verschil_uren > 0 ? '+' : ''}{formatHoursHM(row.verschil_uren)}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums text-slate-600">
                        {row.km_total > 0 ? (
                          <span className="flex items-center justify-end gap-0.5">
                            <MapPin size={11} className="text-slate-400" />
                            {row.km_total.toLocaleString('nl-NL')} km
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow data-testid={`drilldown-row-${row.personeelsnummer}`}>
                        <TableCell colSpan={10} className="p-0">
                          <DrilldownRow
                            employee={row}
                            year={year}
                            week={week}
                            locationId={selectedSlug}
                            onClose={() => setExpanded(null)}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              {/* Totals row */}
              <TableRow className="bg-slate-50 border-t-2 border-slate-200 font-semibold" data-testid="weekoverzicht-totals-row">
                <TableCell></TableCell>
                <TableCell className="text-sm text-slate-900">
                  Totaal ({totals.employees} medew.)
                </TableCell>
                <TableCell className="text-right tabular-nums text-sm text-slate-700">{formatHoursHM(totals.contract_hours)}</TableCell>
                <TableCell className="text-right tabular-nums text-sm text-slate-900">{formatHoursHM(totals.gepland_uren)}</TableCell>
                <TableCell className="text-right tabular-nums text-sm text-emerald-700">{formatHoursHM(totals.gewerkt_uren)}</TableCell>
                <TableCell className="text-right tabular-nums text-sm text-rose-600">{formatHoursHM(totals.ziekte_uren)}</TableCell>
                <TableCell className="text-right tabular-nums text-sm text-sky-600">{formatHoursHM(totals.vakantie_uren)}</TableCell>
                <TableCell className="text-right tabular-nums text-sm text-violet-600">{formatHoursHM(totals.bijz_verlof_uren)}</TableCell>
                <TableCell className={`text-right tabular-nums text-sm font-bold ${diffColor(totals.verschil_uren)}`}>
                  {totals.verschil_uren > 0 ? '+' : ''}{formatHoursHM(totals.verschil_uren)}
                </TableCell>
                <TableCell className="text-right tabular-nums text-sm text-slate-600">
                  {totals.km_total > 0 ? `${totals.km_total.toLocaleString('nl-NL')} km` : '—'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </div>

      {/* Lock confirm dialog */}
      <AlertDialog open={confirmLock} onOpenChange={setConfirmLock}>
        <AlertDialogContent data-testid="lock-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Week definitief maken?</AlertDialogTitle>
            <AlertDialogDescription>
              Week <strong>{week}/{year}</strong> voor <strong>{selectedSlug}</strong> wordt afgesloten.
              Goedkeuringen, shift-bewerkingen en verloning-aanvullingen zijn daarna niet meer mogelijk
              (alleen owner/admin kan ontgrendelen).
              <br /><br />
              {totals.employees || 0} medewerker(s), {formatHoursHM(totals.gewerkt_uren || 0)} gewerkt totaal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lock-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction
              data-testid="lock-confirm"
              onClick={doLock}
              className="bg-brand-navy hover:bg-brand-navy/90 text-white"
            >
              Definitief maken
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
